import { PAYMENT_METHODS, PAYMENT_PROVIDERS } from '@/constants';
import clientOnly from '@/utilities/client-only';
import getContext from '@/utilities/get-context';

const isClient = typeof window !== 'undefined';

const serverPaymentMethod = getContext().__MA__.AB_TEST_PAYMENT_FORM;

const userGateway = isClient && serverPaymentMethod === PAYMENT_METHODS.CHARGEBEE
  ? getContext().__MA__.billing_engine_subscription?.payment_method?.gateway
  : getContext().__MA__.user?.gateway;
const userPaymentProvider = userGateway === 'paypal_express_checkout' || userGateway === 'paypal'
  ? PAYMENT_PROVIDERS.PAYPAL
  : PAYMENT_PROVIDERS.STRIPE;

export default {
  namespaced: false,

  state: {
    paymentMethod: serverPaymentMethod,
    paymentProvider: userPaymentProvider || PAYMENT_PROVIDERS.STRIPE,
    chargebeeInstance: null,
    stripeInstance: null,
    paypalInstance: null,
    autocompleteInstance: null,
    isRecaptchaLoaded: false,
    isChargebeeLoaded: false,
    isPaypalLoaded: false,
    isAutocompleteLoaded: false,

    isCreditCardValid: false,

    validCardNumber: false,
    validCardExpiry: false,
    validCardCvc: false,

    errorCardNumber: '',
    errorCardExpiry: '',
    errorCardCvc: '',

    countryUsa: {},
    countryAustralia: {},

    isOnGracePeriod: false,
    subscriptionScheduledChange: {},
    billingEngineSubscription: {},
    usesOldPaypalSubscription: false,

    isDunning: false,
    isDunningSnackbarClosed: false,
    isDunningPopupClosed: false,

    // benefit system
    referralLink: '',
    referralCode: '',

    // holiday promo
    isHolidayPromo: false,

    isVatIdEstimationError: null,
  },

  getters: {
    isChargebeeFlow(state) {
      return state === PAYMENT_METHODS.CHARGEBEE;
    },
    isOriginalFlow(state) {
      return state !== PAYMENT_METHODS.CHARGEBEE;
    },
    paymentMethod(state) {
      return state.paymentMethod;
    },
    paymentProvider(state) {
      return state.paymentProvider;
    },
    chargebeeInstance(state) {
      return state.chargebeeInstance;
    },
    paypalInstance(state) {
      return state.paypalInstance;
    },
    stripeInstance(state) {
      return state.stripeInstance;
    },
    autocompleteInstance(state) {
      return state.autocompleteInstance;
    },
    isChargebeeLoaded(state) {
      return state.isChargebeeLoaded;
    },
    isPaypalLoaded(state) {
      return state.isPaypalLoaded;
    },
    isRecaptchaLoaded(state) {
      return state.isRecaptchaLoaded;
    },
    isAutocompleteLoaded(state) {
      return state.isAutocompleteLoaded;
    },
    isCreditCardValid(state) {
      return state.isCreditCardValid;
    },
    validCardNumber(state) {
      return state.validCardNumber;
    },
    validCardExpiry(state) {
      return state.validCardExpiry;
    },
    validCardCvc(state) {
      return state.validCardCvc;
    },
    errorCardNumber(state) {
      return state.errorCardNumber;
    },
    errorCardExpiry(state) {
      return state.errorCardExpiry;
    },
    errorCardCvc(state) {
      return state.errorCardCvc;
    },
    countryUsa(state) {
      return state.countryUsa;
    },
    isOnGracePeriod(state) {
      return state.isOnGracePeriod;
    },
    subscriptionScheduledChange(state) {
      return state.subscriptionScheduledChange;
    },
  },

  mutations: {
    SET_PAYMENT_PROVIDER(state, payload) {
      state.paymentProvider = payload;
    },
    SET_CHARGEBEE_INSTANCE(state, payload) {
      state.chargebeeInstance = payload;
    },
    SET_CHARGEBEE_LOADED(state, payload) {
      state.isChargebeeLoaded = payload;
    },
    SET_PAYPAL_INSTANCE(state, payload) {
      state.paypalInstance = payload;
    },
    SET_PAYPAL_LOADED(state, payload) {
      state.isPaypalLoaded = payload;
    },
    SET_STRIPE_INSTANCE(state, payload) {
      state.stripeInstance = payload;
    },
    SET_AUTOCOMPLETE_INSTANCE(state, payload) {
      state.autocompleteInstance = payload;
    },
    SET_RECAPTCHA_LOADED(state, payload) {
      state.isRecaptchaLoaded = payload;
    },
    SET_AUTOCOMPLETE_LOADED(state, payload) {
      state.isAutocompleteLoaded = payload;
    },
    SET_VAILDATED_CARD(state, payload) {
      state.isCreditCardValid = payload;
    },
    SET_VALID_CARD_NUMBER(state, eventPayload) {
      state.errorCardNumber = '';
      if (eventPayload.error) {
        state.errorCardNumber = eventPayload.error.message;
      }

      state.validCardNumber = false;
      if (eventPayload.complete && !eventPayload.error) {
        state.validCardNumber = true;
      }
    },
    SET_VALID_CARD_EXPIRY(state, eventPayload) {
      state.errorCardExpiry = '';
      if (eventPayload.error) {
        state.errorCardExpiry = eventPayload.error.message;
      }

      state.validCardExpiry = false;
      if (eventPayload.complete && !eventPayload.error) {
        state.validCardExpiry = true;
      }
    },
    SET_VALID_CARD_CVC(state, eventPayload) {
      state.errorCardCvc = '';
      if (eventPayload.error) {
        state.errorCardCvc = eventPayload.error.message;
      }

      state.validCardCvc = false;
      if (eventPayload.complete && !eventPayload.error) {
        state.validCardCvc = true;
      }
    },
    RESET_STRIPE_CARD_ERRORS(state) {
      state.errorCardNumber = '';
      state.errorCardExpiry = '';
      state.errorCardCvc = '';
    },
    SET_COUNTRY_USA(state, payload) {
      state.countryUsa = payload;
    },
    SET_COUNTRY_AUSTRALIA(state, payload) {
      state.countryAustralia = payload;
    },
    SET_IS_ON_GRACE_PERIOD(state, payload) {
      state.isOnGracePeriod = payload;
    },
    SET_SUBSCRIPTION_SCHEDULED_CHANGE(state, payload) {
      state.subscriptionScheduledChange = payload;
    },
    SET_BILLING_ENGINE_SUBSCRIPTION(state, payload) {
      state.billingEngineSubscription = payload;
    },
    SET_USES_OLD_PAYPAL_SUBSCRIPTION(state, payload) {
      state.usesOldPaypalSubscription = payload;
    },
    SET_IS_DUNNING(state, payload) {
      state.isDunning = payload;
    },
    SET_IS_DUNNING_SNACKBAR_CLOSED(state, payload) {
      state.isDunningSnackbarClosed = payload;
    },
    SET_IS_DUNNING_POPUP_CLOSED(state, payload) {
      state.isDunningPopupClosed = payload;
    },
    SET_REFERRAL_CODE(state, payload) {
      state.referralCode = payload;
    },
    SET_REFERRAL_LINK(state, payload) {
      state.referralLink = payload;
    },
    SET_IS_HOLIDAY_PROMO(state, payload) {
      state.isHolidayPromo = payload;
    },
    SET_IS_VAT_ID_ESTIMATION_ERROR(state, payload) {
      state.isVatIdEstimationError = payload;
    },
  },

  actions: {
    setPaymentProvider({ commit }, payload) {
      commit('SET_PAYMENT_PROVIDER', payload);
    },
    initSubscriptionScripts({ getters, dispatch }) {
      dispatch('loadReCaptcha');
      if (getters.paymentMethod === PAYMENT_METHODS.CHARGEBEE) {
        dispatch('loadChargebee');
        dispatch('loadPaypal');
      }
    },
    loadReCaptcha({ commit }) {
      clientOnly(() => {
        // Load recaptcha script
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=vueRecaptchaApiLoaded';
        script.async = true;
        document.body.appendChild(script);
        commit('SET_RECAPTCHA_LOADED', true);
      });
    },
    loadChargebee({ dispatch }) {
      clientOnly(() => {
        // Load chargebee script
        const ele = document.createElement('script');
        ele.setAttribute('src', 'https://js.chargebee.com/v2/chargebee.js');
        ele.async = true;
        document.head.appendChild(ele);

        ele.onload = () => {
          dispatch('initChargebee');
        };
      });
    },
    loadPaypal({ dispatch }) {
      clientOnly(() => {
        // Load paypal script
        const paypalClientID = process.env.MIX_BILLING_ENGINE_PAYPAL_CLIENT_ID;
        const ele = document.createElement('script');
        ele.setAttribute(
          'src',
          `https://www.paypal.com/sdk/js?client-id=${paypalClientID}&disable-funding=card,credit,bancontact`,
        );
        ele.async = true;
        document.head.appendChild(ele);

        ele.onload = () => {
          dispatch('initPaypal');
        };
      });
    },
    loadPlaceAutocomplete({ dispatch }) {
      clientOnly(() => {
        // Load google place autocomplete script
        if (!this.isAutocompleteLoaded) {
          const ele = document.createElement('script');
          ele.setAttribute('src', `https://maps.googleapis.com/maps/api/js?key=${process.env.MIX_GOOGLE_PLACES_AUTOCOMPLETE_KEY}&libraries=places&v=weekly`);
          ele.async = true;
          ele.defer = true;
          document.head.appendChild(ele);
          ele.onload = () => {
            dispatch('initAutocomplete');
          };
        }
      });
    },
    initAutocomplete({ state, commit }) {
      if (isClient) {
        try {
          // avoid disabling the autocomplete input field even if API fails to load
          window.gm_authFailure = () => false;
          window.gm_authFailure = () => {
            document.getElementById('pricing-billing-address').disabled = false;
            document.getElementById('pricing-billing-address').placeholder = '';
            // hide error icon
            document.getElementById('pricing-billing-address').style = 'background: none';
          };

          const billingAddressField = document.querySelector('#pricing-billing-address');
          const autocompleteInstance = new window.google.maps.places.Autocomplete(billingAddressField, {
            componentRestrictions: { country: ['us'] },
            fields: ['address_components'],
            types: ['address'],
          });

          commit('SET_AUTOCOMPLETE_INSTANCE', autocompleteInstance);
          commit('SET_AUTOCOMPLETE_LOADED', true);
        } catch (error) {
          commit('SET_AUTOCOMPLETE_LOADED', false);
        }
      }
    },

    initChargebee({ commit }) {
      if (isClient) {
        window.Chargebee.init({
          site: process.env.MIX_BILLING_ENGINE_CHARGEBEE_SITE,
          publishableKey: process.env.MIX_BILLING_ENGINE_CHARGEBEE_PUBLISHABLE_KEY,
        });
      }
      const chargebeeInstance = isClient ? window.Chargebee.getInstance() : null;
      commit('SET_CHARGEBEE_INSTANCE', chargebeeInstance);
      commit('SET_CHARGEBEE_LOADED', true);
    },
    initPaypal({ commit }) {
      if (isClient) {
        const paypalInstance = window.paypal;
        commit('SET_PAYPAL_INSTANCE', paypalInstance);
        commit('SET_PAYPAL_LOADED', true);
      }
    },
    initStripe({ commit }) {
      const stripeInstance = isClient
        ? window.Stripe(process.env.MIX_STRIPE_PUBLISHABLE_KEY)
        : null;
      commit('SET_STRIPE_INSTANCE', stripeInstance);
    },

    // TODO: Move form fields and validation methods here

    // card validation
    validateCard({ commit, getters }) {
      if (!getters.validCardNumber || !getters.validCardExpiry || !getters.validCardCvc) {
        commit('SET_VAILDATED_CARD', false);
      } else {
        commit('SET_VAILDATED_CARD', true);
      }
    },
    validateCardNumber({ commit }, eventPayload) {
      commit('SET_VALID_CARD_NUMBER', eventPayload);
    },
    validateCardExpiry({ commit }, eventPayload) {
      commit('SET_VALID_CARD_EXPIRY', eventPayload);
    },
    validateCardCvc({ commit }, eventPayload) {
      commit('SET_VALID_CARD_CVC', eventPayload);
    },
    resetStripeCardErrors({ commit }) {
      commit('RESET_STRIPE_CARD_ERRORS');
    },
    setCountryUsa({ commit }, payload) {
      commit('SET_COUNTRY_USA', payload);
    },
    setCountryAustralia({ commit }, payload) {
      commit('SET_COUNTRY_AUSTRALIA', payload);
    },
    setIsOnGracePeriod({ commit }, payload) {
      commit('SET_IS_ON_GRACE_PERIOD', payload);
    },
    setSubscriptionScheduledChange({ commit }, payload) {
      commit('SET_SUBSCRIPTION_SCHEDULED_CHANGE', payload);
    },
    setBillingEngineSubscription({ commit }, payload) {
      commit('SET_BILLING_ENGINE_SUBSCRIPTION', payload);
    },
    setUsesOldPaypalSubscription({ commit }, payload) {
      commit('SET_USES_OLD_PAYPAL_SUBSCRIPTION', payload);
    },
    setIsDunning({ commit }, payload) {
      commit('SET_IS_DUNNING', payload);
    },
    setIsDunningSnackbarClosed({ commit }, payload) {
      commit('SET_IS_DUNNING_SNACKBAR_CLOSED', payload);
    },
    setIsDunningPopupClosed({ commit }, payload) {
      commit('SET_IS_DUNNING_POPUP_CLOSED', payload);
    },
    setReferralCode({ commit }, payload) {
      commit('SET_REFERRAL_CODE', payload);
    },
    setReferralLink({ commit }, payload) {
      commit('SET_REFERRAL_LINK', payload);
    },
    // holiday promotion
    setIsHolidayPromo({ commit }, payload) {
      commit('SET_IS_HOLIDAY_PROMO', payload);
    },
    setIsVatIdEstimationError({ commit }, payload) {
      commit('SET_IS_VAT_ID_ESTIMATION_ERROR', payload);
    },
  },
};
